import { makeAutoObservable, runInAction } from 'mobx'
import { v1 } from 'uuid'
import React from 'react'
import { AlertDialog } from '../components/Modal'

export const MODAL_STORE = 'modalStore'

export class ModalData {
  readonly id = v1()
  readonly title: string
  readonly content: string
  readonly okLabel: string
  readonly abortLabel?: string
  readonly promise: Promise<boolean>
  isOpen = true
  private resolvePromise!: (value: boolean) => void

  constructor(
    title: string,
    content: string,
    { okLabel = 'Ok', abortLabel }: { okLabel?: string; abortLabel?: string } = {}
  ) {
    this.title = title
    this.content = content
    this.okLabel = okLabel
    this.abortLabel = abortLabel
    this.promise = new Promise((resolve) => {
      this.resolvePromise = resolve
    })
    makeAutoObservable(this, { promise: false })
  }

  close = (value: boolean) => {
    this.resolvePromise(value)
    this.isOpen = false
  }
}

export class ModalStore {
  private _modals: Array<ModalData> = []

  constructor() {
    makeAutoObservable(this)
  }

  closeModal = (id: string, value: boolean) => {
    const modal = this._modals.find((m) => m.id === id)
    if (!modal) throw new Error('Modal of provided id does not exist')
    modal.close(value)
    // Keep the modal mounted briefly so its close animation can play out.
    setTimeout(() => {
      runInAction(() => {
        this._modals = this._modals.filter((m) => m.id !== id)
      })
    }, 200)
  }

  /// returns modal promise
  openModal = (modal: ModalData): Promise<boolean> => {
    this._modals.push(modal)
    return modal.promise
  }

  get hasModal(): boolean {
    return Boolean(this._modals.length)
  }

  /// returns modal components
  get modals(): React.ReactNode {
    return this._modals.map((modal) => (
      <AlertDialog
        key={modal.id}
        isOpen={modal.isOpen}
        onClose={(value) => this.closeModal(modal.id, value)}
        title={modal.title}
        content={modal.content}
        okLabel={modal.okLabel}
        abortLabel={modal.abortLabel}
      />
    ))
  }
}
